import { LucideCheck, LucideLayoutGrid } from "lucide-react";

/**
 * Linear or two-level step indicator used by report form pages.
 *
 * Props:
 *   steps          - Array<{ id, title, group?: { stepIds: id[], jumpTo: id } }>
 *                    A step with `group` renders a grid-icon node whose done/active
 *                    state is derived from its children.
 *   subSteps       - Optional Array<{ id, title }>
 *                    When provided, renders a persistent pill sub-row below the top
 *                    row. Also switches the top row to compact sizing.
 *   currentStep    - Currently active step id
 *   completedSteps - Set<id> of completed step ids
 *   onStepClick    - Called with step id when a bubble is clicked
 */
export function StepIndicator({ steps, subSteps, currentStep, completedSteps, onStepClick }) {
  const compact = !!(subSteps?.length);

  return (
    <div>
      {/* ── Top row ── */}
      <div className="flex items-start w-full">
        {steps.map((step, index) => {
          const isGroup = !!step.group;
          const isLast = index === steps.length - 1;

          const isDone = isGroup
            ? step.group.stepIds.every((id) => completedSteps.has(id))
            : completedSteps.has(step.id);
          const isCurrent = isGroup
            ? step.group.stepIds.includes(currentStep)
            : step.id === currentStep;

          const handleClick = () =>
            isGroup ? onStepClick(step.group.jumpTo) : onStepClick(step.id);

          return (
            <div
              key={String(step.id)}
              className="flex items-center"
              style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}
            >
              <div
                className="flex flex-col items-center"
                style={{ minWidth: compact ? "28px" : "32px" }}
              >
                <button
                  type="button"
                  onClick={handleClick}
                  title={step.title}
                  className={[
                    "rounded-full flex items-center justify-center border-2 font-semibold transition-all cursor-pointer shrink-0",
                    compact ? "w-7 h-7 text-[11px] font-bold" : "w-8 h-8 text-sm",
                    isDone && !isCurrent
                      ? "bg-primary border-primary text-primary-foreground"
                      : isCurrent
                      ? "bg-primary border-primary text-primary-foreground scale-110 ring-4 ring-inset ring-primary/30"
                      : "bg-input border-border text-muted-foreground hover:bg-accent",
                  ].join(" ")}
                >
                  {isDone && !isCurrent ? (
                    <LucideCheck size={compact ? 11 : 14} />
                  ) : isGroup ? (
                    <LucideLayoutGrid size={compact ? 12 : 13} />
                  ) : (
                    step.id
                  )}
                </button>
                <span
                  className={[
                    "text-center leading-tight",
                    compact ? "text-[9px] mt-0.5" : "text-xs mt-1",
                    isCurrent
                      ? "text-primary font-bold"
                      : isDone
                      ? "text-primary/70 font-medium"
                      : "text-muted-foreground font-medium",
                  ].join(" ")}
                  style={compact ? { maxWidth: "44px", wordBreak: "break-word" } : undefined}
                >
                  {step.title}
                </span>
              </div>
              {!isLast && (
                <div
                  className={[
                    "flex-1 mx-0.5 mb-4 bg-border",
                    compact ? "h-px" : "h-0.5",
                  ].join(" ")}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Sub-row (optional) ── */}
      {subSteps?.length > 0 && (
        <div className="mt-2 mx-1 rounded-xl bg-muted/50 border border-border px-4 py-2">
          <div className="flex items-start w-full">
            {subSteps.map((step, index) => {
              const isDone = completedSteps.has(step.id);
              const isCurrent = step.id === currentStep;
              const isLast = index === subSteps.length - 1;
              return (
                <div
                  key={String(step.id)}
                  className="flex items-center"
                  style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}
                >
                  <div className="flex flex-col items-center" style={{ minWidth: "22px" }}>
                    <button
                      type="button"
                      onClick={() => onStepClick(step.id)}
                      title={step.title}
                      className={[
                        "w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-[1.5px] transition-all cursor-pointer shrink-0",
                        isDone && !isCurrent
                          ? "bg-primary border-primary text-primary-foreground"
                          : isCurrent
                          ? "bg-primary border-primary text-primary-foreground scale-125 ring-2 ring-inset ring-primary/20"
                          : "bg-input border-border text-muted-foreground",
                      ].join(" ")}
                    >
                      {isDone && !isCurrent ? <LucideCheck size={8} /> : step.id}
                    </button>
                    <span
                      className={[
                        "text-[8px] mt-0.5 text-center leading-tight",
                        isCurrent
                          ? "text-primary font-bold"
                          : isDone
                          ? "text-primary/50 font-medium"
                          : "text-muted-foreground font-medium",
                      ].join(" ")}
                      style={{ maxWidth: "44px", wordBreak: "break-word" }}
                    >
                      {step.title}
                    </span>
                  </div>
                  {!isLast && (
                    <div className="h-px flex-1 mx-0.5 mb-3.5 bg-border" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
